// HeatMap.vue
<template>
  <div
    ref="heatmap"
    class="heatmap-layer"
    :class="{ 'heatmap-layer--interactive': interactive }"
    :style="`width: ${mapWidth}; height: ${mapHeight}`"/>
</template>

<script>
import { createGoogleHeatmapOverlay } from '@/utils/googleHeatmapOverlay'
import { computeDenseClusterView } from '@/utils/heatmapViewBounds'

const DEFAULT_CENTER = { lat: 39.9042, lng: 116.4074 }

export default {
  name: 'c-heat-map',
  props: {
    lat: {
      type: Number,
      default: () => 37.775
    },
    lng: {
      type: Number,
      default: () => -122.434
    },
    initialZoom: {
      type: Number,
      default: () => 13
    },
    mapType: {
      type: String,
      default: () => 'roadmap'
    },
    points: {
      type: Array,
      required: true
    },
    width: {
      type: [String, Number],
      default: () => '100%'
    },
    height: {
      type: [String, Number],
      default: () => '100%'
    },
    opacity: {
      type: Number,
      default: () => 1
    },
    maxIntensity: {
      type: Number,
      default: () => 5
    },
    radius: {
      type: Number,
      default: () => 25
    },
    interactive: {
      type: Boolean,
      default: false
    },
    autoFitBounds: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    mapWidth () {
      if (typeof this.width === 'string') {
        return this.width
      } else {
        return `${this.width}px`
      }
    },
    mapHeight () {
      if (typeof this.height === 'string') {
        return this.height
      } else {
        return `${this.height}px`
      }
    }
  },
  watch: {
    points: {
      deep: true,
      handler () {
        this.updateHeatmapData()
        if (this.autoFitBounds) {
          this.scheduleFitBounds()
        }
      }
    },
    maxIntensity () {
      if (this.$heatmap) {
        this.$heatmap.setMaxIntensity(this.maxIntensity)
      }
    },
    radius () {
      if (this.$heatmap) {
        this.$heatmap.setRadius(this.radius)
      }
    },
    opacity () {
      if (this.$heatmap) {
        this.$heatmap.setOpacity(this.opacity)
      }
    },
    lat () {
      if (!this.interactive) {
        this.recenterMap()
      }
    },
    lng () {
      if (!this.interactive) {
        this.recenterMap()
      }
    }
  },
  mounted () {
    this.initHeatmap()
  },
  beforeDestroy () {
    this._isDestroyed = true
    if (this._fitBoundsTimer) {
      clearTimeout(this._fitBoundsTimer)
    }
    if (this._initRetryTimer) {
      clearTimeout(this._initRetryTimer)
    }
    if (this._resizePulseTimers) {
      this._resizePulseTimers.forEach((id) => clearTimeout(id))
      this._resizePulseTimers = null
    }
    if (this.$heatmap) {
      this.$heatmap.setMap(null)
      this.$heatmap = null
    }
    this.$mapObject = null
  },
  methods: {
    getMapCenter () {
      const lat = Number(this.lat)
      const lng = Number(this.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
        return { ...DEFAULT_CENTER }
      }
      return { lat, lng }
    },
    // 容器尚未布局完时创建 Map 会导致瓦片只铺一角；等有宽高再 init
    initHeatmap (attempt = 0) {
      if (this._isDestroyed) return
      const el = this.$refs.heatmap
      if (!el || el.offsetWidth === 0 || el.offsetHeight === 0) {
        if (attempt < 12) {
          this._initRetryTimer = setTimeout(() => this.initHeatmap(attempt + 1), attempt === 0 ? 50 : 120)
        }
        return
      }
      return this.$gmapApiPromiseLazy().then(() => {
        if (this._isDestroyed) return
        if (this.$mapObject) {
          this.refreshMapView()
          return
        }
        const google = window.google
        if (!google || !google.maps) return

        const elNow = this.$refs.heatmap
        if (!elNow || elNow.offsetWidth === 0 || elNow.offsetHeight === 0) {
          if (attempt < 12) {
            this._initRetryTimer = setTimeout(() => this.initHeatmap(attempt + 1), 120)
          }
          return
        }

        const center = this.getMapCenter()
        this.$mapObject = new google.maps.Map(elNow, {
          zoom: this.initialZoom,
          center,
          mapTypeId: this.mapType,
          disableDefaultUI: true,
          gestureHandling: this.interactive ? 'greedy' : 'none',
          draggable: this.interactive,
          scrollwheel: this.interactive,
          keyboardShortcuts: false
        })

        this.$heatmap = createGoogleHeatmapOverlay(google, {
          map: this.$mapObject,
          points: this.points,
          maxIntensity: this.maxIntensity,
          radius: this.radius,
          opacity: this.opacity
        })

        this.$nextTick(() => {
          if (this._isDestroyed || !this.$mapObject) return
          this.refreshMapView()
          // 父级 flex/图表稍后重排时再补几次 resize
          this._resizePulseTimers = [80, 200, 500].map((ms) =>
            setTimeout(() => {
              if (!this._isDestroyed) this.refreshMapView()
            }, ms)
          )
          this.$emit('ready')
        })
      })
    },
    scheduleFitBounds () {
      if (this._fitBoundsTimer) {
        clearTimeout(this._fitBoundsTimer)
      }
      this._fitBoundsTimer = setTimeout(() => {
        this._fitBoundsTimer = null
        this.fitBoundsToPoints()
      }, 120)
    },
    fitBoundsToPoints () {
      if (!this.$mapObject || this._isDestroyed) return Promise.resolve(false)
      const google = window.google
      if (!google?.maps) return Promise.resolve(false)

      // 数据总览等场景：框网格主聚集区，避免全量点 fitBounds 缩太远导致热力不可见
      const view = computeDenseClusterView(this.points || [])
      const validPoints = (this.points || []).filter((point) => {
        const lat = Number(point.lat)
        const lng = Number(point.lng)
        return Number.isFinite(lat) && Number.isFinite(lng)
      })

      if (!validPoints.length) {
        this.$mapObject.setCenter(view.center)
        this.$mapObject.setZoom(view.suggestedZoom)
        return Promise.resolve(true)
      }

      if (validPoints.length === 1) {
        this.$mapObject.setCenter(view.center)
        this.$mapObject.setZoom(view.suggestedZoom)
        return Promise.resolve(true)
      }

      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(view.sw.lat, view.sw.lng),
        new google.maps.LatLng(view.ne.lat, view.ne.lng)
      )
      this.$mapObject.fitBounds(bounds, 50)
      // fitBounds 异步生效后：若仍过远则抬到 suggestedZoom，保证热力可见
      google.maps.event.addListenerOnce(this.$mapObject, 'idle', () => {
        if (this._isDestroyed || !this.$mapObject) return
        const z = Number(this.$mapObject.getZoom())
        if (Number.isFinite(z) && z < view.suggestedZoom) {
          this.$mapObject.setZoom(view.suggestedZoom)
        }
        if (this.$heatmap) this.$heatmap.draw()
      })
      return Promise.resolve(true)
    },
    refreshMapView () {
      if (!this.$mapObject || this._isDestroyed) return false
      const google = window.google
      if (!google?.maps) return false
      const el = this.$refs.heatmap
      if (!el || el.offsetWidth === 0 || el.offsetHeight === 0) return false
      google.maps.event.trigger(this.$mapObject, 'resize')
      if (this.autoFitBounds && this.points.length) {
        this.fitBoundsToPoints()
      } else {
        this.$mapObject.setCenter(this.getMapCenter())
      }
      if (this.$heatmap) {
        this.$heatmap.draw()
      }
      return true
    },
    updateHeatmapData () {
      if (!this.$heatmap || this._isDestroyed) return
      this.$heatmap.setPoints(this.points)
    },
    recenterMap () {
      if (!this.$mapObject || this._isDestroyed) return
      this.$mapObject.setCenter(this.getMapCenter())
      if (this.$heatmap) {
        this.$heatmap.draw()
      }
    },
    // 供数据总览等外层「+/-」按钮调用（与实时监控 changeGoogleZoom 一致）
    changeZoom (delta) {
      if (!this.$mapObject || this._isDestroyed) return false
      const current = Number(this.$mapObject.getZoom())
      const base = Number.isFinite(current) ? current : this.initialZoom
      const nextZoom = Math.min(22, Math.max(3, base + delta))
      this.$mapObject.setZoom(nextZoom)
      const google = window.google
      if (google?.maps) {
        google.maps.event.addListenerOnce(this.$mapObject, 'idle', () => {
          if (!this._isDestroyed && this.$heatmap) this.$heatmap.draw()
        })
      } else if (this.$heatmap) {
        this.$heatmap.draw()
      }
      return true
    }
  }
}
</script>

<style scoped>
.heatmap-layer {
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.heatmap-layer--interactive {
  pointer-events: auto;
}
</style>
